import type { Options } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import { trackComparePlanClick } from '@ecomm/cdp-tracking'
import { useLocale } from '@ecomm/data-hooks'
import {
  coreMonitoringPlanData,
  proMonitoringPlanData,
  proPlusMonitoringPlanData
} from '@ecomm/maps/data/planData'
import { useMicroCopy } from '@ecomm/micro-copy'
import { ContentfulRichText } from '@ecomm/shared-components/lib/ContentfulRichText'
import { useTrackOdmonComparePlanClick } from '@ecomm/tracking/useTrackOdmonComparePlanClick'
import { useState } from 'react'
import ComparisonTable from '../ComparisonTable'
import { MonitoringCards } from '../MonitoringCards'
import { MonitoringCard } from '../MonitoringCards/MonitoringCard'
import { ChangeBusinessUnit } from '../MonitoringChoice/ChangeBusinessUnit'
import MonitoringPlanModal from '../MonitoringPlanModal'
import type { MonitoringPlanModalSchema } from '../MonitoringPlanModal/schema'
import type { OdmonExpandableMonitoringSchema } from './schema'
import { TableData } from './tableData'

const options: Options = {
  renderNode: {
    [BLOCKS.TABLE]: node => {
      return <ComparisonTable {...TableData} />
    },
    [INLINES.EMBEDDED_ENTRY]: node => {
      const {
        data: { target }
      } = node
      const data: MonitoringPlanModalSchema = target

      return <MonitoringPlanModal {...data} withAddToCartButton={true} />
    }
  }
}

export function OdmonExpandableMonitoring(
  data: OdmonExpandableMonitoringSchema
) {
  const {
    headline,
    subheadline,
    comparisonTable,
    comparisonDisclaimer,
    showAddToCartButton
  } = data
  const trackComparePlan = useTrackOdmonComparePlanClick()
  const [comparisonTableVisible, setComparisonTableVisible] = useState(false)
  const microcopy = useMicroCopy()
  const locale = useLocale()

  return (
    <div className="prose md:prose-md lg:prose-lg flex w-full flex-col items-center gap-6 md:gap-4">
      {headline ? (
        <h2 className="mb-0 text-center text-3xl md:max-w-[960px] md:text-6xl">
          {headline}
        </h2>
      ) : null}
      {subheadline ? (
        <p className="max-w-[860px] text-center text-base">{subheadline}</p>
      ) : null}

      <ChangeBusinessUnit isBusiness={false}></ChangeBusinessUnit>
      <MonitoringCards>
        <MonitoringCard
          plan={coreMonitoringPlanData}
          showCartCTA={showAddToCartButton}
        />
        <MonitoringCard
          plan={proMonitoringPlanData}
          showCartCTA={showAddToCartButton}
        />
        <MonitoringCard
          plan={proPlusMonitoringPlanData}
          showCartCTA={showAddToCartButton}
        />
      </MonitoringCards>
      {comparisonTableVisible ? (
        <ContentfulRichText {...comparisonTable} optionsCustom={options} />
      ) : (
        <button
          className="btn btn-outlined btn-outlined-primary mx-auto cursor-pointer border-none bg-transparent mt-8 p-0 text-lg text-blue-600 underline hover:text-white"
          onClick={() => {
            setComparisonTableVisible(true)
            trackComparePlanClick()
            trackComparePlan()
          }}
        >
          {microcopy['compare-plans']}
        </button>
      )}

      {comparisonDisclaimer && comparisonTableVisible && locale !== 'en-GB' ? (
        <div className="border-neutral-light-100 rounded-base border border-solid p-6 md:p-8">
          <ContentfulRichText
            {...comparisonDisclaimer}
            optionsCustom={options}
          />
        </div>
      ) : null}
    </div>
  )
}
